import type { Placement } from '@popperjs/core';
import cn from 'classnames';
import { cloneElement, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import { CloseBtn } from '@webapp/common/components/close-btn';
import { OutsideClick } from '@webapp/common/components/outside-click';
import { Actions } from '@webapp/common/components/popup/actions';
import { nop } from '@webapp/common/lib/utils';

import css from './popup.css';

// TODO split tooltip and modal

export type PopupProps = {
    title?: ReactNode;
    className?: string;
    closeBtn?: boolean;
    position?: 'left' | 'center';
    trigger?: ReactElement;
    onOpen?: () => void;
    onClose?: () => void;
    onRemove?: () => void;
    onPurge?: () => void;
    onCancel?: () => void;
    onAccept?: () => void;
    cancelText?: string;
    acceptText?: string;
    style?: CSSProperties;
    placement?: Placement;
    modal?: boolean;
    titleAlign?: 'center' | 'left' | 'right';
    visible?: boolean;
};

export const Popup: FC<PopupProps> = ({
    acceptText,
    cancelText,
    children,
    className,
    closeBtn,
    onAccept,
    onCancel,
    onClose = nop,
    onOpen = nop,
    onPurge,
    onRemove,
    placement = 'auto',
    style,
    title,
    trigger,
    modal = false,
    titleAlign = 'center',
    visible: visibleProp,
}) => {
    const [visible, setVisible] = useState(false);
    const handleClose = useCallback(() => {
        setVisible(false);
        onClose();
    }, [onClose]);
    const [triggerRef, setTriggerRef] = useState<HTMLDivElement | null>(null);
    const [popupRef, setPopupRef] = useState<HTMLDivElement | null>(null);
    const {
        attributes,
        forceUpdate,
        styles: stylesPopper,
        update,
    } = usePopper(triggerRef, popupRef, {
        placement,
        strategy: modal ? 'absolute' : 'fixed',
        modifiers: [{ name: 'preventOverflow', enabled: true }],
    });

    useEffect(() => {
        if (typeof visibleProp === 'boolean' && visibleProp !== visible) {
            setVisible(visibleProp);
        }
    }, [visibleProp]);

    const togglePopup = useCallback((): void => {
        setVisible((v) => {
            v = !v;

            if (v) {
                onOpen();
            } else {
                onClose();
            }

            return v;
        });
    }, [onClose, onOpen]);

    const renderTrigger = useCallback(
        (): ReactElement =>
            cloneElement(trigger, {
                key: 'T',
                ref: setTriggerRef,
                onClick: togglePopup,
            }),
        [togglePopup, trigger],
    );

    const handleClickCancel = useCallback((): void => {
        setVisible(false);

        if (onCancel) {
            onCancel();
        }

        onClose();
    }, [onCancel, onClose]);

    const handleClickAccept = (): void => {
        setVisible(false);
        onAccept();

        if (visible) {
            onClose();
        }
    };

    const handleClickRemove = useCallback((): void => {
        setVisible(false);

        if (onRemove) {
            onRemove();
        }

        onClose();
    }, [onClose, onRemove]);

    const handleClickPurge = useCallback((): void => {
        setVisible(false);
        onPurge();
        onClose();
    }, [onClose, onPurge]);

    useEffect(() => {
        if (forceUpdate && update) {
            const intID = setTimeout(() => {
                document.getElementById('app').children[0].scrollTop += 100;
                document.body.scrollTop += 0.01;
                void update();
            }, 300);
            return () => clearInterval(intID);
        }
    }, [forceUpdate, update]);

    useLayoutEffect(() => {
        setTimeout(() => window.dispatchEvent(new Event('scroll')), 10);
    }, []);

    const content = (
        <OutsideClick
            active={visible}
            className={cn(css.popup, { [css.hidden]: trigger && !visible }, className)}
            ref={setPopupRef}
            style={{ ...style, ...stylesPopper.popper }}
            onClickOutside={handleClose}
            triggerRef={triggerRef}
            {...attributes.popper}
        >
            {title && (
                <div className={css.title} style={{ textAlign: titleAlign }}>
                    {title}
                </div>
            )}
            {closeBtn && <CloseBtn className={css.close} onClick={handleClickCancel} />}
            {children}
            {(onRemove || onCancel || onAccept) && (
                <Actions
                    acceptText={acceptText}
                    cancelText={cancelText}
                    onAccept={onAccept && handleClickAccept}
                    onCancel={onCancel && handleClickCancel}
                    onPurge={onPurge && handleClickPurge}
                    onRemove={onRemove && handleClickRemove}
                />
            )}
        </OutsideClick>
    );

    return (
        <>
            {trigger && renderTrigger()}
            {modal ? content : createPortal(content, document.querySelector('#popup2'))}
        </>
    );
};
